import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getTickets, callNextTicket, completeTicket, cancelTicket,
  getTicketStats, logout, createWebSocket,
} from '../services/api';
import './StaffDashboard.css';

function StaffDashboard() {
  const [user, setUser] = useState(null);
  const [staffProfile, setStaffProfile] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [currentTicket, setCurrentTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('waiting');
  const navigate = useNavigate();
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const reconnectDelay = useRef(1000);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    const staffData = localStorage.getItem('staff_profile');
    if (!userData || !staffData) { navigate('/staff/login'); return; }
    setUser(JSON.parse(userData));
    setStaffProfile(JSON.parse(staffData));
  }, [navigate]);

  const loadTickets = useCallback(async () => {
    if (!staffProfile) return;
    try {
      const res = await getTickets({ faculty: staffProfile.faculty, status: filter, today: true });
      const list = res.data.results || res.data;
      setTickets(list);
      if ((filter === 'called' || filter === 'serving') && list.length > 0) {
        setCurrentTicket(list[0]);
      } else if (filter !== 'called' && filter !== 'serving') {
        setCurrentTicket(null);
      }
    } catch (e) {
      console.error('Failed to load tickets:', e);
    }
  }, [staffProfile, filter]);

  const loadStats = useCallback(async () => {
    if (!staffProfile) return;
    try {
      const res = await getTicketStats(staffProfile.faculty);
      setStats(res.data);
    } catch (e) {
      console.error('Failed to load stats:', e);
    }
  }, [staffProfile]);

  // WebSocket with reconnection
  const connectWS = useCallback(() => {
    if (!staffProfile) return;
    if (wsRef.current) { try { wsRef.current.close(); } catch(e){} }

    const ws = createWebSocket(staffProfile.faculty);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket connected');
      reconnectDelay.current = 1000; // reset backoff
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'ticket_update') {
          loadTickets();
          loadStats();
        }
      } catch(e) {}
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected, reconnecting...');
      reconnectTimer.current = setTimeout(() => {
        reconnectDelay.current = Math.min(reconnectDelay.current * 2, 30000);
        connectWS();
      }, reconnectDelay.current);
    };

    ws.onerror = () => {}; // onclose will fire after
  }, [staffProfile, loadTickets, loadStats]);

  useEffect(() => {
    if (!staffProfile) return;
    loadTickets();
    loadStats();
    setLoading(false);
    connectWS();

    const interval = setInterval(() => { loadTickets(); loadStats(); }, 10000);

    return () => {
      clearInterval(interval);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch(e){} }
    };
  }, [staffProfile, connectWS, loadTickets, loadStats]);

  const handleCallNext = async (ticketId) => {
    try {
      const res = await callNextTicket(ticketId);
      setCurrentTicket(res.data);
      toast.success(`Navbat ${res.data.ticket_number} chaqirildi!`);
      loadTickets(); loadStats();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Chaqirishda xatolik');
    }
  };

  const handleComplete = async (ticketId) => {
    try {
      await completeTicket(ticketId, '');
      setCurrentTicket(null);
      toast.success('Xizmat tugatildi');
      loadTickets(); loadStats();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Tugatishda xatolik');
    }
  };

  const handleCancel = async (ticketId) => {
    try {
      await cancelTicket(ticketId);
      setCurrentTicket(null);
      toast.success('Navbat bekor qilindi');
      loadTickets(); loadStats();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Bekor qilishda xatolik');
    }
  };

  const handleLogout = async () => {
    try { await logout(); } catch(e) {}
    localStorage.removeItem('user');
    localStorage.removeItem('staff_profile');
    navigate('/staff/login');
  };

  const badgeClass = (s) => `badge badge-${s}`;

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="staff-dashboard">
      <div className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <h1>Xodim oynasi</h1>
              <p>
                {user?.first_name} {user?.last_name} |
                Counter: {staffProfile?.counter_number} |
                Faculty: {staffProfile?.faculty_name}
              </p>
            </div>
            <div className="header-right">
              <button className="btn btn-secondary" onClick={handleLogout}>Chiqish</button>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        {stats && (
          <div className="stats-grid">
            <div className="stat-card stat-waiting">
              <div className="stat-value">{stats.total_waiting}</div>
              <div className="stat-label">Kutilmoqda</div>
            </div>
            <div className="stat-card stat-called">
              <div className="stat-value">{stats.total_called}</div>
              <div className="stat-label">Chaqirilgan</div>
            </div>
            <div className="stat-card stat-serving">
              <div className="stat-value">{stats.total_serving}</div>
              <div className="stat-label">Jarayonda</div>
            </div>
            <div className="stat-card stat-served">
              <div className="stat-value">{stats.total_served}</div>
              <div className="stat-label">Bugun xizmat ko'rsatilganlar</div>
            </div>
            <div className="stat-card stat-avg">
              <div className="stat-value">{stats.average_wait_time} min</div>
              <div className="stat-label">O'rtacha kutish vaqti</div>
            </div>
          </div>
        )}

        {currentTicket && (
          <div className="card current-ticket-card">
            <h2>Hozirgi Navbat</h2>
            <div className="current-ticket-display">
              <div className="ticket-number-large">{currentTicket.ticket_number}</div>
              <div className="ticket-details">
                <p><strong>Xizmat:</strong> {currentTicket.service_name}</p>
                <p><strong>Bo'lim:</strong> {currentTicket.section_name}</p>
                <p><strong>Holat:</strong> <span className={badgeClass(currentTicket.status)}>{currentTicket.status}</span></p>
                {currentTicket.student_name && <p><strong>Talaba:</strong> {currentTicket.student_name}</p>}
              </div>
              <div className="ticket-actions">
                {(currentTicket.status === 'called' || currentTicket.status === 'serving') && (
                  <button className="btn btn-primary" onClick={() => handleComplete(currentTicket.id)}>Tugatish</button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="filter-tabs">
            {[
              ['waiting', 'Kutilmoqda', stats?.total_waiting],
              ['called', 'Chaqirilgan', stats?.total_called],
              ['serving', 'Jarayonda', stats?.total_serving],
              ['served', 'Tugatilgan', stats?.total_served],
            ].map(([key, label, count]) => (
              <button key={key} className={`filter-tab ${filter === key ? 'active' : ''}`}
                onClick={() => setFilter(key)}>
                {label} ({count || 0})
              </button>
            ))}
          </div>

          <div className="tickets-list">
            {tickets.length === 0 ? (
              <div className="no-tickets"><p>Navbatlar yo'q</p></div>
            ) : (
              <div className="tickets-table">
                <table>
                  <thead>
                    <tr>
                      <th>Navbat #</th>
                      <th>Xizmat</th>
                      <th>Bo'lim</th>
                      <th>Talaba</th>
                      <th>Vaqt</th>
                      <th>Holat</th>
                      <th>Amallar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((t) => (
                      <tr key={t.id}>
                        <td className="ticket-number-cell">{t.ticket_number}</td>
                        <td>{t.service_name}</td>
                        <td>{t.section_name}</td>
                        <td>{t.student_name || '-'}</td>
                        <td>{new Date(t.created_at).toLocaleTimeString()}</td>
                        <td><span className={badgeClass(t.status)}>{t.status}</span></td>
                        <td>
                          {t.status === 'waiting' && (
                            <>
                              <button className="btn btn-sm btn-primary" onClick={() => handleCallNext(t.id)}>Chaqirish</button>
                              {' '}
                              <button className="btn btn-sm btn-danger" onClick={() => handleCancel(t.id)}>Bekor</button>
                            </>
                          )}
                          {(t.status === 'called' || t.status === 'serving') && (
                            <>
                              <button className="btn btn-sm btn-warning" onClick={() => handleComplete(t.id)}>Tugatish</button>
                              {' '}
                              <button className="btn btn-sm btn-danger" onClick={() => handleCancel(t.id)}>Bekor</button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StaffDashboard;
